/**
 * Field.js
 *
 * The form-input primitive of the Lantern design system (ChoreStory). One
 * component drives every labelled control across the Ledger + entry surfaces it
 * appears on (login / signup / auth / settings / quest-studio / family /
 * insights / approvals / platform-admin) — wiring a `<label>`, a control, an
 * optional hint, and an inline error into one accessible unit that reads the
 * surrounding register + theme tokens (`--surface-1`, `--line`, `--accent`,
 * `--text-hi`, `--rose-500`, …). A single `<Field>` is parchment-and-amber on
 * the light entry doors and ink-and-river in the dark parent Ledger with no
 * per-surface variants in the consumer. Mirrors the mockups' `.field` family
 * (auth/login/signup) plus `.txt` (quest-studio), `.l-select` (settings/
 * insights) and `.search` (platform-admin); see design-brief §5.5.
 *
 * Anatomy:
 *   Label  (required ·)                                 ← <label for=…>
 *   [lead]  control text                       [trail]  ← input | textarea | select
 *   Helper hint sits quietly underneath.                ← optional hint
 *   ⚠ Something needs a look — here's why.               ← optional inline error
 *
 * `variants: []` (component-inventory#Field) — there is no visual `variant`
 * axis. The component varies along three orthogonal axes instead:
 *   - the CONTROL it renders: `as="input"` (default, any `type`), `"textarea"`,
 *     or `"select"` (inferred as select when `options` is passed, textarea when
 *     `multiline` is set);
 *   - SIZE: `md` (default — 44px target, honoring brief §2.6) or `sm` (≈36px,
 *     dense Ledger forms like the family invite row / platform-admin search);
 *   - STATE: the per-screen specs flag `hover`, `loading` and `error` as MISSING
 *     for this component, so all three are defined here.
 *
 * States: default, hover (calm border lift), focus-visible (accent ring +
 * border), disabled, plus the flagged-missing pair —
 *   - error: conventional Ledger inline error (rose border + `aria-invalid` +
 *     a rose message wired as the control's `aria-describedby`). Per brief §2.7
 *     this is the CALM, never-panics form of error — a polite live region, NOT
 *     `role="alert"`; entry surfaces use Ledger form semantics (rose permitted)
 *     with Hearth-warm copy. Kid Hearth surfaces use the lantern-dim setback
 *     pattern instead and should not pass `error`.
 *   - loading: a trailing spinner adornment + `aria-busy` + a polite SR cue,
 *     for async validation / populating a value. Presentational only — it does
 *     NOT disable the control (pair with `disabled` if edits must be blocked),
 *     so focus is never stolen.
 *
 * Self-contained per the implement plan (one file per component, no sibling
 * .css): styles ship once via useInsertionEffect under a namespaced `cfd-*`
 * class set, every value `var(--token, fallback)` so it also renders calmly
 * standalone. Matches the sibling Button.js / Fab.js conventions exactly.
 */

import React, { useId, useInsertionEffect } from 'react';
const safeRest = (o) => { if (!o || typeof o !== "object") return o; const clean = (h) => (typeof h === "string" && /^(javascript|vbscript|data):/.test(h.trim().toLowerCase().replace(/[\s\u0000-\u001f]+/g, "")) ? undefined : h); const { dangerouslySetInnerHTML, srcDoc, srcdoc, ...r } = o; for (const k of ["href", "src", "action", "formAction", "poster"]) if (k in r) r[k] = clean(r[k]); return r; };

/* ------------------------------------------------------------------ *
 * Vocabulary                                                          *
 * ------------------------------------------------------------------ */

const CONTROLS = new Set(['input', 'textarea', 'select']);
const SIZES = new Set(['sm', 'md']);

/* ------------------------------------------------------------------ *
 * Dev-only guards (never throw, fire at most once per message)        *
 * ------------------------------------------------------------------ */

const warnedKeys = new Set();

/**
 * Dev-only, never-throwing warning. Fires under Node/test/bundlers that define
 * `process.env.NODE_ENV`; stays silent in production and in browser bundles
 * without a `process` global.
 */
function devWarn(key, message) {
  if (warnedKeys.has(key)) return;
  try {
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.NODE_ENV !== 'production' &&
      typeof console !== 'undefined'
    ) {
      warnedKeys.add(key);
      console.warn(message);
    }
  } catch (_) {
    /* no-op: warnings must never break rendering */
  }
}

/**
 * Resolve a user-facing error string from a string | Error | error-like object,
 * falling back to `fallback` when nothing meaningful is present. Guards against
 * an empty `new Error('')` being silently swallowed and a plain object
 * stringifying to "[object Object]". Mirrors Button.js so error handling reads
 * identically across the system.
 */
function resolveErrorMessage(error, fallback) {
  if (error == null || error === false) return null;
  if (error === true) return fallback;
  let raw;
  if (typeof error === 'string') {
    raw = error;
  } else if (error instanceof Error) {
    raw = error.message;
  } else if (typeof error === 'object') {
    raw = error.message != null ? String(error.message) : '';
  } else {
    raw = String(error);
  }
  return raw.trim() || fallback;
}

/* ------------------------------------------------------------------ *
 * Scoped stylesheet (injected once)                                   *
 * ------------------------------------------------------------------ */

const STYLE_ID = 'cfd-styles';
const STYLES = `
.cfd {
  display: grid;
  gap: var(--s-2, 8px);
  text-align: left;
  min-width: 0;
}

/* ---- label ---- */
.cfd__label {
  font: 600 var(--fs-sm, .9rem) var(--font-ui, "Inter", system-ui, -apple-system, "Segoe UI", sans-serif);
  color: var(--text-hi, #f4f1e8);
  line-height: 1.3;
}
.cfd__req {
  color: var(--rose-500, #c43d33);
  margin-left: 2px;
}
.cfd__optional {
  color: var(--text-low, #8b93b8);
  font-weight: 500;
  margin-left: 6px;
}
/* visually-hidden label (kept in the a11y tree) */
.cfd__label--hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* ---- control wrapper (hosts absolute adornments) ---- */
.cfd__field {
  position: relative;
  display: flex;
  min-width: 0;
}

/* ---- the control itself ---- */
.cfd__el {
  font: 500 var(--fs-base, 1rem) var(--font-ui, "Inter", system-ui, sans-serif);
  color: var(--text-hi, #f4f1e8);
  width: 100%;
  min-width: 0;
  min-height: 44px;
  padding: 0 var(--s-4, 16px);
  background: var(--surface-1, #121831);
  border: 1.5px solid var(--line, rgba(139,147,184,.22));
  border-radius: var(--r-sm, 8px);
  -webkit-appearance: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  transition: border-color var(--motion-quick, 150ms) var(--ease-out-soft, ease),
              box-shadow var(--motion-quick, 150ms) var(--ease-out-soft, ease),
              background var(--motion-quick, 150ms) var(--ease-out-soft, ease);
}
.cfd__el::placeholder { color: var(--text-low, #8b93b8); opacity: 1; }
.cfd__el::-ms-input-placeholder { color: var(--text-low, #8b93b8); }

/* hover (a MISSING state, defined here): a calm border lift, never a glow.
   Suppressed for disabled / busy controls. */
.cfd__el:hover:not(:disabled):not([aria-busy="true"]) {
  border-color: color-mix(in oklab, var(--accent, #fbbf24) 50%, var(--line, rgba(139,147,184,.22)));
}

/* focus: accent border + a two-tone ring that stays delineated on any surface
   (WCAG 2.4.7 / 1.4.11). The transparent outline is the forced-colors fallback
   (box-shadow is dropped there; a transparent outline is repainted by the OS as
   a visible system focus ring). */
.cfd__el:focus-visible {
  border-color: var(--accent, #fbbf24);
  outline: 2px solid transparent;
  outline-offset: 0;
  box-shadow: 0 0 0 3px color-mix(in oklab, var(--accent, #fbbf24) 32%, transparent);
}
/* legacy focus fallback for engines without :focus-visible */
.cfd__el:focus:not(:focus-visible) { border-color: var(--accent, #fbbf24); }

/* textarea sits taller and grows vertically */
.cfd__el--textarea {
  min-height: 96px;
  padding: var(--s-3, 12px) var(--s-4, 16px);
  line-height: 1.5;
  resize: vertical;
}

/* select: native chevron suppressed, painted as a calm background caret */
.cfd__el--select {
  padding-right: var(--s-8, 32px);
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238b93b8' stroke-width='1.8' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right var(--s-3, 12px) center;
}
/* when a trailing adornment (spinner / icon) is present the caret is dropped so
   the two never overlap. */
.cfd__el--nocaret { background-image: none; }

/* adornment padding (only applied when a slot is filled) */
.cfd__el--padlead { padding-left: var(--s-12, 44px); }
.cfd__el--padtrail { padding-right: var(--s-12, 44px); }

/* ---- size: sm (dense Ledger forms) ---- */
.cfd__el--sm {
  min-height: 36px;
  padding: 0 var(--s-3, 12px);
  font-size: var(--fs-sm, .9rem);
}
.cfd__el--sm.cfd__el--textarea { min-height: 72px; padding: var(--s-2, 8px) var(--s-3, 12px); }
.cfd__el--sm.cfd__el--select { padding-right: var(--s-6, 24px); }
.cfd__el--sm.cfd__el--padlead { padding-left: var(--s-8, 32px); }
.cfd__el--sm.cfd__el--padtrail { padding-right: var(--s-8, 32px); }

/* ---- error: conventional Ledger inline error (calm, never panics §2.7) ---- */
.cfd__el[aria-invalid="true"] {
  border-color: var(--rose-500, #c43d33);
}
.cfd__el[aria-invalid="true"]:focus-visible {
  box-shadow: 0 0 0 3px color-mix(in oklab, var(--rose-500, #c43d33) 30%, transparent);
}

/* ---- disabled ---- */
.cfd__el:disabled {
  cursor: not-allowed;
  opacity: .55;
  -webkit-text-fill-color: currentColor; /* keep value legible on iOS */
}

/* ---- busy (loading): control stays interactive, just dims a touch ---- */
.cfd__el[aria-busy="true"] { opacity: .85; }

/* ---- adornments (absolutely positioned inside the field box) ---- */
.cfd__adorn {
  position: absolute;
  top: 0;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  pointer-events: none; /* never block the control under it */
  color: var(--text-low, #8b93b8);
  line-height: 0;
}
.cfd__adorn--lead { left: var(--s-3, 12px); }
.cfd__adorn--trail { right: var(--s-3, 12px); }
.cfd__adorn svg { display: block; }

.cfd__spinner {
  width: 1em;
  height: 1em;
  border-radius: 50%;
  border: 2px solid color-mix(in oklab, currentColor 28%, transparent);
  border-top-color: currentColor;
  /* visible as a partial ring even when motion is disabled */
}

/* ---- hint ---- */
.cfd__hint {
  margin: 0;
  font: 500 var(--fs-xs, .75rem) var(--font-ui, "Inter", system-ui, sans-serif);
  color: var(--text-low, #8b93b8);
  line-height: 1.4;
}

/* ---- inline error message (rose, Ledger error semantics) ---- */
.cfd__error {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  font: 600 var(--fs-xs, .75rem) var(--font-ui, "Inter", system-ui, sans-serif);
  color: var(--rose-500, #c43d33);
  line-height: 1.4;
}
.cfd__error svg { flex: none; margin-top: 1px; }

/* ---- visually-hidden (live region) ---- */
.cfd__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: no-preference) {
  .cfd__spinner { animation: cfd-spin .7s linear infinite; }
  @keyframes cfd-spin { to { transform: rotate(360deg); } }
}

/* Respect reduced motion everywhere (brief §2.4): drop control transitions; the
   spinner is already static here (its animation is gated above). */
@media (prefers-reduced-motion: reduce) {
  .cfd__el { transition: none; }
}

/* Forced-colors / Windows high-contrast: keep the box and its states legible
   when authored colors are replaced by the system palette. */
@media (forced-colors: active) {
  .cfd__el { border-color: ButtonText; }
  .cfd__el:disabled { border-color: GrayText; opacity: 1; color: GrayText; }
  .cfd__el[aria-invalid="true"] { border-color: Mark; }
}

/* AD-08 touch-target floor (tokens.json targets.minimumCssPx = 44). Below the rail breakpoint
   every control here meets 44 CSS px; desktop geometry is unchanged. Measured in Chromium at 375
   before and after: docs/reviews/p05-p10/jobs/p05-23-fable-02/evidence. */
@media (max-width: 1023.98px) {
  .cfd__el--sm { min-height: var(--target-min, 44px); }
}
`;

/** Inject the component's scoped stylesheet once per document. */
function useFieldStyles() {
  useInsertionEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (document.getElementById(STYLE_ID)) return undefined;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = STYLES;
    document.head.appendChild(el);
    return undefined;
  }, []);
}

/** Small warning glyph for the inline error row (rose, Ledger error semantics). */
function ErrorGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16.5v.01" />
    </svg>
  );
}

/**
 * Normalize `options` (string[] | {value,label,disabled}[]) into <option>s for
 * the select control. Returns null when nothing usable is supplied (the host's
 * `children` are used instead).
 */
function renderOptions(options) {
  if (!Array.isArray(options) || options.length === 0) return null;
  return options.map((opt, i) => {
    if (opt != null && typeof opt === 'object') {
      // A ReactNode `label` can't serialize to a usable `value` attribute, so only
      // borrow the label as the value when it is a primitive; otherwise fall back
      // to an empty value rather than coercing a node to "[object Object]". Key by
      // the explicit value when present (stable across reorders), else the index.
      const hasValue = opt.value != null;
      const labelIsPrimitive = typeof opt.label === 'string' || typeof opt.label === 'number';
      const value = hasValue ? opt.value : labelIsPrimitive ? opt.label : '';
      return (
        <option
          key={hasValue ? `v-${String(opt.value)}` : `opt-${i}`}
          value={value}
          disabled={opt.disabled}
        >
          {opt.label != null ? opt.label : String(value)}
        </option>
      );
    }
    return (
      <option key={`opt-${i}`} value={opt}>
        {String(opt)}
      </option>
    );
  });
}

/**
 * Field — the Lantern form-input primitive.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.label]  Visible label. Omit only when an
 *        explicit `aria-label`/`aria-labelledby` is supplied (dev-warns otherwise).
 * @param {'input'|'textarea'|'select'} [props.as]  Control element. Defaults to
 *        'input', or 'select' when `options` is given, or 'textarea' when
 *        `multiline` is set.
 * @param {string} [props.type]  Input type (input mode only). Default 'text'.
 * @param {Array<string|{value:any,label:React.ReactNode,disabled?:boolean}>} [props.options]
 *        Convenience options for `as="select"`. Ignored if `children` are given.
 * @param {boolean} [props.multiline]  Shorthand for `as="textarea"`.
 * @param {'sm'|'md'} [props.size]  Control size. Default 'md' (44px target,
 *        brief §2.6). 'sm' (≈36px) is for dense PARENT Ledger forms only.
 * @param {React.ReactNode} [props.hint]  Quiet helper text below the control.
 * @param {string|Error|object|boolean|null} [props.error]  Inline error (rose,
 *        below the control; sets `aria-invalid`). Accepts string/Error/object/
 *        `true` (uses `errorFallback`). Ledger surfaces only.
 * @param {string} [props.errorFallback]  Message shown when `error` is truthy
 *        but carries no usable text. Default "Something needs a look — try again."
 * @param {boolean} [props.loading]  Busy state: trailing spinner + `aria-busy` +
 *        a polite SR cue. Presentational only — does NOT disable the control.
 * @param {string} [props.busyLabel]  SR-only busy announcement. Default "Working…".
 * @param {boolean} [props.disabled]  Standard disabled state.
 * @param {boolean} [props.required]  Marks the control required + shows a `*`.
 * @param {boolean} [props.optional]  Shows a quiet "(optional)" label suffix
 *        (ignored when `required`).
 * @param {boolean} [props.hideLabel]  Visually hide the `label` while keeping it
 *        in the accessibility tree. REQUIRES `label` text — it does NOT synthesize
 *        a name. Pass BOTH (e.g. `label="Search tenants"` + `hideLabel`) for the
 *        platform-admin search box, or supply an `aria-label` if no `label` exists.
 * @param {React.ReactNode} [props.iconLeading]  Decorative leading adornment.
 * @param {React.ReactNode} [props.iconTrailing]  Decorative trailing adornment
 *        (replaced by the spinner while loading; the select caret is dropped
 *        when present).
 * @param {React.ReactNode} [props.children]  For `as="select"`, the <option>s.
 * @param {string} [props.id]  Control id (auto-generated when omitted). Used to
 *        wire the label, hint and error together.
 * @param {string} [props.className]        Extra classes for the wrapper.
 * @param {string} [props.controlClassName]  Extra classes for the control.
 * @param {string} [props.labelClassName]    Extra classes for the <label>.
 * @param {React.Ref} [props.innerRef]       Ref to the underlying control.
 * @param {Object} [props.rest]  Remaining props forwarded to the control
 *        (`name`, `value`, `defaultValue`, `onChange`, `placeholder`,
 *        `autoComplete`, `rows`, `aria-*`, `data-*`, …). Spread first so the
 *        component's controlled props (id, a11y wiring, disabled) win.
 *
 * The wrapper carries `data-component="Field"`, `data-as`, `data-size` and a
 * runtime `data-state` ("loading" | "disabled" | "error" | "default") as stable
 * hooks for tests and host-level styling.
 */
export function Field({
  label,
  as,
  type = 'text',
  options,
  multiline = false,
  size = 'md',
  hint,
  error = null,
  errorFallback = 'Something needs a look — try again.',
  loading = false,
  busyLabel = 'Working…',
  disabled = false,
  required = false,
  optional = false,
  hideLabel = false,
  iconLeading,
  iconTrailing,
  children,
  id,
  className = '',
  controlClassName = '',
  labelClassName = '',
  innerRef,
  ...rest
}) {
  useFieldStyles();

  // Stable ids for label / hint / error wiring. useId is SSR-safe and stable
  // across renders; a host-provided `id` takes precedence for the control.
  const autoId = useId();
  const controlId = id != null && id !== '' ? id : `${autoId}-control`;
  const hintId = `${autoId}-hint`;
  const errorId = `${autoId}-error`;

  // Resolve the control element. Explicit `as` wins; otherwise infer from
  // `options` (select) / `multiline` (textarea), defaulting to input.
  let resolvedAs = as;
  if (resolvedAs == null) {
    resolvedAs = options != null ? 'select' : multiline ? 'textarea' : 'input';
  } else if (!CONTROLS.has(resolvedAs)) {
    devWarn(
      `cfd-as-${resolvedAs}`,
      `Field: unknown control "${resolvedAs}" — falling back to "input". ` +
        `Expected one of: input, textarea, select.`,
    );
    resolvedAs = 'input';
  }

  const resolvedSize = SIZES.has(size) ? size : 'md';

  const errorMessage = resolveErrorMessage(error, errorFallback);
  // error and loading are mutually exclusive (mirrors Button.js): a field that
  // is busy revalidating shouldn't also paint a stale rose error.
  const showError = !!errorMessage && !loading;
  const isBusy = !!loading;

  // An accessible name is required. Best-effort dev HINT: we can only inspect an
  // explicit label / aria-label / aria-labelledby / title — a visually-hidden
  // text node passed some other way would satisfy AT without tripping this. When
  // `hideLabel` is set without a `label`, the message is specific: hideLabel only
  // visually hides an EXISTING label, it never creates a name.
  const hasName = !(label == null && !rest['aria-label'] && !rest['aria-labelledby'] && !rest.title);
  if (!hasName) {
    devWarn(
      hideLabel ? 'cfd-hidelabel-noname' : 'cfd-noname',
      hideLabel
        ? 'Field: `hideLabel` was set but no `label` was provided — hideLabel only ' +
            'visually hides an existing `label` (kept for AT), it does not create an ' +
            'accessible name. Pass `label` (rendered SR-only) or an `aria-label`.'
        : 'Field: rendered without a `label` — provide one (or an `aria-label`/' +
            'aria-labelledby/title) so the control has an accessible name.',
    );
  }

  // Trailing slot: spinner while busy, else the host's trailing icon. A select's
  // painted caret is suppressed whenever this slot is occupied so they can't
  // collide.
  const trailingContent = isBusy ? (
    <span className="cfd__spinner" aria-hidden="true" />
  ) : iconTrailing != null ? (
    iconTrailing
  ) : null;
  const hasLeading = iconLeading != null;
  const hasTrailing = trailingContent != null;

  // Compose the describedby chain, preserving any host-provided value. Order:
  // host → hint → error, so AT reads helper context before the error.
  const describedBy =
    [rest['aria-describedby'], hint != null ? hintId : null, showError ? errorId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  const elClass = [
    'cfd__el',
    `cfd__el--${resolvedAs}`,
    resolvedSize === 'sm' ? 'cfd__el--sm' : '',
    hasLeading ? 'cfd__el--padlead' : '',
    hasTrailing ? 'cfd__el--padtrail' : '',
    resolvedAs === 'select' && hasTrailing ? 'cfd__el--nocaret' : '',
    controlClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const dataState = isBusy ? 'loading' : disabled ? 'disabled' : showError ? 'error' : 'default';

  // Shared control props. Conditional keys (never explicit `undefined`) so a
  // host value coming through `...rest` survives when we don't override it.
  const sharedProps = {
    ...rest,
    id: controlId,
    className: elClass,
    disabled: disabled || undefined,
  };
  // Ref: honor the documented `innerRef` (house convention shared with Button/Fab).
  // Set as a CONDITIONAL key — not an explicit `undefined` — so that a host using
  // React 19's native ref-as-prop (which arrives via `...rest`) is preserved when
  // `innerRef` is absent. `innerRef` wins when both are supplied.
  if (innerRef != null) sharedProps.ref = innerRef;
  // Accessible-name floor: a form control must never be nameless (WCAG 4.1.2 /
  // axe `label`). When a host supplies a `label`, `aria-label`, `aria-labelledby`
  // or `title` the control is named already (the `<label htmlFor>` below, or the
  // host attr arriving via `...rest`), so we leave it untouched. Only when NONE
  // of those exist — the misuse the dev-warn above already flags — do we attach a
  // generic SR-only name so the control degrades to merely vague rather than
  // unlabelled. Invisible (aria-label paints nothing), no new prop, no behavior
  // change for any correctly-labelled call site.
  if (!hasName) sharedProps['aria-label'] = 'Field';
  if (required) sharedProps.required = true;
  if (showError) sharedProps['aria-invalid'] = 'true';
  if (isBusy) sharedProps['aria-busy'] = 'true';
  if (describedBy) sharedProps['aria-describedby'] = describedBy;

  let control;
  if (resolvedAs === 'textarea') {
    control = <textarea {...safeRest(sharedProps)} />;
  } else if (resolvedAs === 'select') {
    const opts = renderOptions(options);
    control = <select {...safeRest(sharedProps)}>{opts != null ? opts : children}</select>;
  } else {
    // input: `type` is controlled (default 'text'); a host `type` in `...rest`
    // is overridden here so the explicit prop is the single source of truth.
    control = <input {...safeRest(sharedProps)} type={type} />;
  }

  const wrapperClass = ['cfd', className].filter(Boolean).join(' ');
  const labelClass = ['cfd__label', hideLabel ? 'cfd__label--hidden' : '', labelClassName]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={wrapperClass}
      data-component="Field"
      data-as={resolvedAs}
      data-size={resolvedSize}
      data-state={dataState}
    >
      {label != null ? (
        <label className={labelClass} htmlFor={controlId}>
          {label}
          {required ? (
            <span className="cfd__req" aria-hidden="true">
              {' '}
              *
            </span>
          ) : optional ? (
            <span className="cfd__optional">(optional)</span>
          ) : null}
        </label>
      ) : null}

      <div className="cfd__field">
        {hasLeading ? (
          <span className="cfd__adorn cfd__adorn--lead" aria-hidden="true">
            {iconLeading}
          </span>
        ) : null}

        {control}

        {hasTrailing ? (
          <span className="cfd__adorn cfd__adorn--trail" aria-hidden="true">
            {trailingContent}
          </span>
        ) : null}
      </div>

      {hint != null ? (
        <p className="cfd__hint" id={hintId}>
          {hint}
        </p>
      ) : null}

      {/*
        Polite busy cue. Always rendered (empty when idle) so insertions are
        announced reliably via a live region rather than relying on aria-busy
        alone, which AT support for is uneven on form controls.
      */}
      <span className="cfd__sr" aria-live="polite">
        {isBusy ? busyLabel : ''}
      </span>

      {showError ? (
        // A polite live region AND the control's aria-describedby target — one
        // calm announcement channel, NOT role="alert" (assertive interruption
        // fights the describedby read and contradicts the never-panics posture
        // of §2.7). loading and error are mutually exclusive, so this never
        // overlaps the busy cue above.
        <p className="cfd__error" id={errorId} aria-live="polite" aria-atomic="true">
          <ErrorGlyph />
          <span>{errorMessage}</span>
        </p>
      ) : null}
    </div>
  );
}

export default Field;
